import { useEffect, useState } from 'react';
import { Simulator } from './pages/Simulator.tsx';
import { Guest } from './pages/Guest.tsx';
import { Organizer } from './pages/Organizer.tsx';
import { Connect } from './pages/Connect.tsx';
import { probeServer } from './server.ts';

function useRoute(): string {
  const read = () => window.location.hash.replace(/^#/, '') || '/';
  const [route, setRoute] = useState(read);
  useEffect(() => {
    const on = () => setRoute(read());
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);
  return route;
}

type Health = 'pending' | 'up' | { down: string };

/**
 * Any Rally deployment works as the backend. Same-origin servers answer
 * immediately; when the page was built for a server elsewhere (the HF Space
 * build, for example), the Connect screen asks for that URL and remembers it.
 */
function useServerHealth(): Health {
  const [health, setHealth] = useState<Health>('pending');
  useEffect(() => {
    let alive = true;
    probeServer().then((res) => {
      if (alive) setHealth(res.ok ? 'up' : { down: res.detail ?? 'unreachable' });
    });
    return () => {
      alive = false;
    };
  }, []);
  return health;
}

export function App() {
  const route = useRoute();
  const health = useServerHealth();
  if (health === 'pending') return <div className="splash">Rally</div>;
  if (health !== 'up') return <Connect detail={typeof health === 'object' ? health.down : undefined} />;

  const guest = route.match(/^\/r\/([A-Za-z0-9_-]{10,64})$/);
  if (guest) return <Guest token={guest[1]!} />;
  const organizer = route.match(/^\/o\/([A-Za-z0-9_-]{10,64})$/);
  if (organizer) return <Organizer token={organizer[1]!} />;
  return (
    <div className="app">
      <nav className="top">
        <a className="brand" href="#/">
          <span className="logo" aria-hidden="true">●●</span> Rally <span className="brand-sub">for Alexa+</span>
        </a>
        <span className="tagline">Say it once. Rally gets the whole group to the same table.</span>
        <a className="gh" href="https://github.com/Hisernberg/amazon-hackathon-2026" target="_blank" rel="noreferrer">
          Source ↗
        </a>
      </nav>
      <main>
        <Simulator />
      </main>
    </div>
  );
}
